import { unwrap } from '@hive/api';
import { useEffect, useState } from 'react';
import { useSession } from './session';

/** Permissões por tela (catálogo do núcleo: blocos do menu → telas → ver, editar e ações). */
export type ScreenAction = { key: string; perms: string[]; sensitive?: boolean };
export type ScreenDef = { id: string; route: string; nav: string; view: string[]; edit: string[] | null; apis: string[]; actions: ScreenAction[]; managedBy?: 'autonomous' };
export type BlockDef = { id: string; nav: string; icon: string; screens: ScreenDef[] };
/** Tela que o Autonomous assumiu (integração ligada na implantação): rota, nome no menu e por onde os dados saem. */
export type AutonomousScreen = { id: string; route: string; nav: string; screen: string | null; keep: string[]; feeds: string[] };
/** url: endereço do Autonomous (hive.autonomous.url), vazio = sem "Abrir no Autonomous". */
export type ScreenCatalogData = { version: number; blocks: BlockDef[]; autonomous?: { enabled: boolean; screens: AutonomousScreen[]; url?: string } };

/**
 * Catálogo guardado por página, relido quando a página volta a ficar visível depois de {@link MAX_AGE_MS}: a console
 * instalada (PWA) fica aberta dias, e ligar ou desligar a integração com o Autonomous (flag da implantação) muda o
 * menu, as rotas e a matriz sem recarregar a página. O service worker nunca guarda /api (vite-sw.ts).
 */
export const MAX_AGE_MS = 5 * 60_000;

type Fetch = () => Promise<ScreenCatalogData>;
let cache: ScreenCatalogData | null = null;
let fetchedAt = 0;
let failed = false;
let pending: Promise<ScreenCatalogData> | null = null;
let lastFetch: Fetch | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

/** Carrega (ou relê) o catálogo; quem está inscrito recebe o novo. Falha não apaga o que já havia. */
export function loadScreenCatalog(fetch: Fetch, force = false): Promise<ScreenCatalogData | null> {
  lastFetch = fetch;
  if (cache && !force) return Promise.resolve(cache);
  pending ??= fetch()
    .then((c) => {
      cache = c;
      fetchedAt = Date.now();
      failed = false;
      return c;
    })
    .finally(() => {
      pending = null;
    });
  return pending.then(
    (c) => {
      emit();
      return c;
    },
    () => {
      failed = !cache;
      emit();
      return cache;
    },
  );
}

/** Relê se o catálogo tem mais de {@link MAX_AGE_MS} (página voltou a ficar visível, rede voltou). */
export function refreshScreenCatalogIfStale(now = Date.now()): Promise<ScreenCatalogData | null> {
  if (!lastFetch || (cache && now - fetchedAt < MAX_AGE_MS)) return Promise.resolve(cache);
  return loadScreenCatalog(lastFetch, true);
}

export function screenCatalogSnapshot(): { data: ScreenCatalogData | null; failed: boolean } {
  return { data: cache, failed };
}

/** Só para testes: esquece o catálogo da página. */
export function resetScreenCatalog() {
  cache = null;
  fetchedAt = 0;
  failed = false;
  pending = null;
  lastFetch = null;
  listeners.clear();
}

let watching = false;
function watchVisibility() {
  if (watching || typeof document === 'undefined') return;
  watching = true;
  const again = () => {
    if (document.visibilityState === 'visible') void refreshScreenCatalogIfStale();
  };
  document.addEventListener('visibilitychange', again);
  window.addEventListener('online', again);
}

/** Catálogo e se a carga falhou (sem catálogo nenhum): o Shell espera por ele antes de abrir a tela. */
export function useScreenCatalogState(): { data: ScreenCatalogData | null; failed: boolean } {
  const s = useSession();
  const [state, setState] = useState(screenCatalogSnapshot);
  useEffect(() => {
    const on = () => setState((prev) => (prev.data === cache && prev.failed === failed ? prev : screenCatalogSnapshot()));
    listeners.add(on);
    watchVisibility();
    void loadScreenCatalog(() => unwrap(s.api.GET('/api/v1/permissions/screens')) as unknown as Promise<ScreenCatalogData>);
    on();
    return () => {
      listeners.delete(on);
    };
  }, [s.api]);
  return state;
}

/** Esquece o catálogo guardado (testes que trocam a integração com o Autonomous entre um caso e outro). */
export function resetScreenCatalogCache() {
  cache = null;
  pending = null;
}

/** Catálogo carregado uma vez por sessão da página (o menu e o editor de perfil usam o mesmo). */
export function useScreenCatalog(): ScreenCatalogData | null {
  return useScreenCatalogState().data;
}

/** A tela de um caminho: a rota mais longa do catálogo que é o caminho ou o começo dele. */
export function screenOfPath(cat: ScreenCatalogData | null, path: string): ScreenDef | undefined {
  if (!cat) return undefined;
  let best: ScreenDef | undefined;
  for (const b of cat.blocks)
    for (const sc of b.screens)
      if ((path === sc.route || path.startsWith(`${sc.route}/`)) && (!best || sc.route.length > best.route.length)) best = sc;
  return best;
}

/** Com a integração com o Autonomous ligada: a tela dele que cobre o caminho (fora as rotas que ela mantém), se houver. */
export function autonomousOf(cat: ScreenCatalogData | null, path: string): AutonomousScreen | undefined {
  if (!cat?.autonomous?.enabled) return undefined;
  // só o caminho: "/alerts?id=1" e "/alerts#x" são da tela /alerts
  const clean = path.split(/[?#]/, 1)[0] ?? path;
  const under = (p: string, r: string) => p === r || p.startsWith(`${r}/`);
  let best: AutonomousScreen | undefined;
  for (const a of cat.autonomous.screens)
    if (under(clean, a.route) && !a.keep.some((k) => under(clean, k)) && (!best || a.route.length > best.route.length)) best = a;
  return best;
}

/** O objeto do Autonomous que um link aponta: tipo (alert, cve, asset-vulnerabilities ou a própria tela) e id. */
export type AutonomousTarget = { kind: string; id?: string };

/**
 * Tipo e id do objeto de um link que leva a uma tela do Autonomous. A mesma tabela do núcleo (AutonomousMode.target):
 * /alerts/{id} e /alerts?id={id} → alert; /vulnerabilities/cve/{id} → cve; /vulnerabilities/findings?asset={id} → asset-vulnerabilities;
 * o resto → a própria tela, sem id. Só a tela mais específica decide (/alerts/channels é a tela de canais).
 */
export function autonomousTarget(cat: ScreenCatalogData | null, link: string): AutonomousTarget | undefined {
  const [path = '', query = ''] = link.split('#')[0]!.split('?', 2);
  const sc = autonomousOf(cat, path);
  if (!sc) return undefined;
  const dec = (x: string) => {
    try {
      return decodeURIComponent(x);
    } catch {
      return x;
    }
  };
  if (sc.id === 'alerts') {
    const m = /^\/alerts\/([^/]+)$/.exec(path);
    if (m) return { kind: 'alert', id: dec(m[1]!) };
    // /alerts?id= é o endereço do alerta no console (a rota /alerts/:id redireciona para ele; ficha do ativo, SNMP
    // traps, ITSM e Atividades Operacionais usam esse formato)
    const id = new URLSearchParams(query).get('id');
    if (path === '/alerts' && id) return { kind: 'alert', id };
  }
  if (sc.id === 'vulns') {
    const m = /^\/vulnerabilities\/cve\/([^/]+)$/.exec(path);
    if (m) return { kind: 'cve', id: dec(m[1]!) };
    const asset = new URLSearchParams(query).get('asset');
    if (path === '/vulnerabilities/findings' && asset) return { kind: 'asset-vulnerabilities', id: asset };
  }
  return { kind: sc.id };
}

/**
 * "Abrir no Autonomous": endereço do objeto no Autonomous para um link que leva a uma tela que ele assumiu, ou
 * undefined (integração desligada, sem URL configurada ou tela que continua no Hive). A URL pode ser um modelo com
 * {kind} e {id}; sem eles, vira <url>/<kind>/<id>.
 */
export function autonomousHref(cat: ScreenCatalogData | null, link: string): string | undefined {
  const url = cat?.autonomous?.enabled ? (cat.autonomous.url ?? '').trim() : '';
  if (!url) return undefined;
  const tg = autonomousTarget(cat, link);
  if (!tg) return undefined;
  const id = tg.id ? encodeURIComponent(tg.id) : '';
  // tela inteira (sem id): "{kind}/{id}" vira ".../vulns", sem a barra final (como o núcleo)
  if (url.includes('{kind}') || url.includes('{id}')) return (id ? url : url.replaceAll('/{id}', '')).replaceAll('{kind}', tg.kind).replaceAll('{id}', id);
  return `${url.replace(/\/+$/, '')}/${tg.kind}${id ? `/${id}` : ''}`;
}
